import { useEffect, useRef, useState } from "react"
import type { ProductCardData } from "../types"
import { useCart } from "../cart-context"
import styles from "../styles/widget.module.css"

// Accept either a string ("19.99") or a number (19.99) and render "$19.99".
// Returns null when the input doesn't parse cleanly — we'd rather hide the
// row than display "$NaN" if upstream sends garbage.
function formatPrice(raw: string | number | undefined): string | null {
  if (raw == null) return null
  if (typeof raw === "string" && raw.startsWith("$")) return raw
  const n = typeof raw === "number" ? raw : parseFloat(raw)
  if (!Number.isFinite(n)) return null
  return `$${n.toFixed(2)}`
}

export function ProductCard({ data }: { data: ProductCardData }) {
  const { addToCart } = useCart()
  const variants = data.variants ?? []
  const [selIdx, setSelIdx] = useState(() =>
    Math.max(0, variants.findIndex((v) => v.available)),
  )
  // Reset the selection to the first available variant whenever the variant
  // set changes — otherwise a re-used card instance (same position, new
  // product) would keep a stale index pointing at the wrong/absent variant.
  const variantKey = variants.map((v) => v.id).join(",")
  useEffect(() => {
    setSelIdx(Math.max(0, variants.findIndex((v) => v.available)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantKey])

  if (variants.length === 0) {
    // ===== EXISTING display-only <a> card, UNCHANGED =====
    const price = formatPrice(data.price)
    const compareAt = formatPrice(data.compareAtPrice)
    // Only show the strikethrough when it's actually higher than the live
    // price — Shopify sometimes returns equal compareAtPrice on non-sale
    // items, which would look like a fake-sale gimmick if rendered.
    const showCompareAt =
      !!price &&
      !!compareAt &&
      parseFloat(String(data.compareAtPrice)) >
        parseFloat(String(data.price))

    return (
      // Same-tab navigation — Pearl persists across page loads via the
      // localStorage session_token + identity flag, so the customer keeps
      // their chat (and doesn't have to re-enter their email) after viewing
      // the product. Opening in a new tab forced a fresh sessionStorage in
      // the new tab which kicked the identity gate back on. Bad UX.
      <a
        href={data.productUrl}
        className={styles.rc}
      >
        {data.imageUrl && (
          <img src={data.imageUrl} alt={data.name} className={styles["rc-image"]} />
        )}
        <div className={styles["rc-body"]}>
          <div className={styles["rc-title"]}>{data.name}</div>
          {/* `summary` (the product description) was intentionally dropped on
            * 2026-06-03 per Justin — every product result card was wasting
            * 2-3 lines of vertical space restating what the title already
            * implied, and that ate density (canvas could only fit 4 cards
            * instead of 6-8). The title + price is enough; the customer
            * clicks through for detail. */}
          {price && (
            <div className={styles["rc-price-row"]}>
              <span className={styles["rc-price"]}>{price}</span>
              {showCompareAt && (
                <span className={styles["rc-price-compare"]}>{compareAt}</span>
              )}
            </div>
          )}
          {/* Visible "tap me to navigate" cue. The whole card is already an
            * <a>, so this is decorative — but without it customers don't
            * realise tiles are tappable, especially in the canvas grid
            * where the card chrome reads as a brochure. Caught 2026-06-03:
            * Justin asked for a 'Show me' / 'Take me to it' chip per
            * product. Inline link arrow keeps the row dense (no extra
            * button row required). */}
          {data.productUrl && (
            <div className={styles["rc-cta-row"]}>
              <span className={styles["rc-cta-link"]}>View product &rarr;</span>
            </div>
          )}
        </div>
      </a>
    )
  }

  // ===== INTERACTIVE path — variants present =====
  // NOTE: NOT wrapped in <a> — buttons inside an anchor is invalid HTML
  // and would navigate away on tap. Use a <div> as the outer container.
  const sel = variants[selIdx] ?? variants[0]
  const price = formatPrice(sel.price)
  const fits = data.fitment?.[0]

  // Add-to-cart button state: idle → adding (spinner) → added (check) → idle
  const [cartState, setCartState] = useState<"idle" | "adding" | "added">("idle")
  const revertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Respect prefers-reduced-motion for the scale pulse.
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches

  const handleAddToCart = async () => {
    if (!sel?.available || cartState !== "idle") return
    setCartState("adding")
    try {
      await addToCart(sel.id, 1)
    } finally {
      setCartState("added")
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current)
      revertTimerRef.current = setTimeout(() => setCartState("idle"), 1500)
    }
  }

  // Clean up the revert timer on unmount.
  useEffect(() => {
    return () => {
      if (revertTimerRef.current) clearTimeout(revertTimerRef.current)
    }
  }, [])

  const addBtnClass =
    cartState === "added"
      ? `${styles["rc-add"]} ${styles["rc-add-added"]}${!prefersReducedMotion ? ` ${styles["rc-add-pulse"]}` : ""}`
      : styles["rc-add"]

  return (
    <div className={styles.rc}>
      {data.imageUrl && <img src={data.imageUrl} alt={data.name} className={styles["rc-image"]} />}
      <div className={styles["rc-body"]}>
        <div className={styles["rc-title"]}>{data.name}</div>
        {fits && (
          <div className={styles["rc-fitment"]}>Fits {fits.model} ({fits.years})</div>
        )}
        <div className={styles["rc-variants"]}>
          {variants.map((v, i) => (
            <button
              key={v.id}
              type="button"
              disabled={!v.available}
              onClick={() => setSelIdx(i)}
              className={
                i === selIdx
                  ? `${styles["rc-pill"]} ${styles["rc-pill-on"]}`
                  : styles["rc-pill"]
              }
            >
              {v.title}
            </button>
          ))}
        </div>
        <div className={styles["rc-price-row"]}>
          {price && <span className={styles["rc-price"]}>{price}</span>}
        </div>
        <button
          type="button"
          className={addBtnClass}
          disabled={!sel?.available || cartState !== "idle"}
          onClick={() => { void handleAddToCart() }}
          // While the spinner shows, the button's only content is an
          // aria-hidden span → no accessible name (a11y audit 2026-06-11).
          aria-label={cartState === "adding" ? "Adding to cart" : undefined}
          aria-busy={cartState === "adding" || undefined}
        >
          {cartState === "adding" ? (
            <span className={styles["rc-add-spinner"]} aria-hidden="true" />
          ) : cartState === "added" ? (
            "Added ✓"
          ) : (
            "Add to cart"
          )}
        </button>
      </div>
    </div>
  )
}
